'use client';
import React, { useState, MouseEvent, useRef, useEffect, useCallback } from 'react';
import { L1Item } from './NAV_TYPES';
import SubMenu from './sub-menu';
import { handleize } from 'src/scripts/utils/helpers';
import { fireGA4MenuClickEvent } from 'src/scripts/utils/gtm-data-layer';

type MenuItemProps = {
  link: L1Item;
};

export const handleHeapTracking = (event: MouseEvent<HTMLAnchorElement>) => {
  const breadcrumbs = event.currentTarget.getAttribute('data-nav');
  heap?.track('Desktop Nav Click', { breadcrumbs });
};

function MenuItem({ link }: MenuItemProps) {
  const linkTitleHandle = handleize(link.title);
  const [isHovered, setIsHovered] = useState(false);
  const titleColor = link?.color || 'var(--primary)';
  const inlineNavLinkStyles = {
    color: link?.color,
    // this is needed so that the :after underline on the nav link is able to inherit the color
    '--nav-title-color': titleColor,
  } as React.CSSProperties;

  const mainRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    mainRef.current = document.querySelector('main');
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    mainRef.current?.setAttribute('style', '--main-overlay-visibility: visible;');
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    mainRef.current?.setAttribute('style', '--main-overlay-visibility: hidden;');
  }, []);

  return (
    <li
      className={`main-nav__item--wrapper navOpenOnHover ${isHovered ? 'hover' : ''}`}
      tabIndex={0}
      id={linkTitleHandle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-controls-menu={true}
    >
      {link.url ? (
        <a
          href={link.url}
          className={`main-nav__item hover-target uppercase`}
          data-nav={link.title.toLowerCase()}
          onClick={(e) => {
            handleHeapTracking(e);
            fireGA4MenuClickEvent(link.title);
          }}
          style={inlineNavLinkStyles}
        >
          {link.title}
          {link.titleTag && <span className="menu-item-badge">{link.titleTag}</span>}
        </a>
      ) : (
        <span
          className={`main-nav__item hover-target uppercase`}
          data-nav={link.title.toLowerCase()}
          style={inlineNavLinkStyles}
        >
          {link.title}
          {link.titleTag && <span className="menu-item-badge">{link.titleTag}</span>}
        </span>
      )}

      {link?.items?.length && <SubMenu link={link} isActive={isHovered} />}
    </li>
  );
}

export default MenuItem;
